/**
 * Customers ("לקוחות") — the CRM's main screen, route "/".
 *
 * What it does
 *   - Search customers by free text, tags, statuses, name, phone, mail, sex and date range
 *   - Results table: sort by name / status / tag / date entered, change a customer's status
 *     inline, call / WhatsApp / message / edit / delete per row
 *   - Bulk actions on the checked rows: change status, add a tag, send a message
 *   - Download the results as CSV, import a list of customers, add a customer
 *   - Opens <CustomerDrawer> to view/edit one customer (history, emails, tags…)
 *
 * Data & side effects (all unchanged from the pre-redesign screen):
 *   searchCustomers(filters)            POST — search; also called once with {} on mount to collect
 *                                       all emails/phones for the duplicate check in the editor
 *   getDISTINCTListFromDB(customerStatus) statuses for the inline status picker
 *   updateCustomerInfo({key:'status_id'}) inline + bulk status change
 *   POST /api/tags/:id/customers        bulk add-tag
 *   deleteFromList(id, 'customers')     delete (after withConfirmAlert)
 *   setShowSendMassage(true, "id,id"[, "WHATSAPP"])  opens the global send-message modal
 *                                       (row WhatsApp icon opens it on the WhatsApp tab)
 *
 * Selection model (kept from the old screen): each result row carries `check`.
 * After every search ALL rows are checked, exactly like before; bulk actions act on
 * checked rows. The table's checkbox column simply reads/writes those flags.
 *
 * UI: built only from the shared kit in src/ui (see client/UI_GUIDE.md).
 */
import React, { useState, useEffect } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { CSVLink } from "react-csv";
import {
  PlusOutlined,
  MoreOutlined,
  PhoneOutlined,
  WhatsAppOutlined,
  MessageOutlined,
  EditOutlined,
  DeleteOutlined,
  TagOutlined,
  SwapOutlined,
  DownloadOutlined,
  UploadOutlined,
} from "@ant-design/icons";

import * as actionsCreators from "./../../actions/index.js";
import {
  Page,
  PageHeader,
  FilterBar,
  FilterField,
  DataTable,
  RowActions,
  BulkActionBar,
  StatusBadge,
  TagChips,
  EmptyState,
  Button,
  Input,
  Select,
  DatePicker,
  Dropdown,
  Modal,
  Tooltip,
  LIST,
  useLookupList,
  toOptions,
  itemLabel,
  statusColor,
  useWhatsappStatus,
  useBreakpoint,
  tokens,
} from "../../ui";
import dateToString from "./../../utils/dateToString.js";
import validationPhoneNumber from "./../../utils/validationPhoneNumber.js";
import createHeaders from "./../../utils/headers_for_customers.js";
import CustomerDrawer from "./CustomerDrawer.js";
import InsertList from "./../insertList/insertList.js";
import dayjs from "dayjs";

import classes from "./Customers.css";

const headers = createHeaders();

// Phones/tablets: the "call" row action (tel: link) is only shown there.
const IS_TOUCH = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer: coarse)").matches;

// Native <input type="date"> values were "YYYY-MM-DD"; the DatePicker keeps the same format.
const DATE_FORMAT = "YYYY-MM-DD";

const Customers = (props) => {
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false); // UI only: first-visit vs "no results" empty state
  const [showUpdater, setShowUpdater] = useState(false);
  const [showUpdaterFlag, setShowUpdaterFlag] = useState("");
  const [showChangeListStatus, setShowChangeListStatus] = useState(false);
  const [statusForChange, setStatusForChange] = useState("");
  const [statusIdForChange, setStatusIdForChange] = useState(null);
  const [showChangeListTags, setShowChangeListTags] = useState(false);
  const [selectedTagId, setSelectedTagId] = useState(null);
  const [trackingInfo, settrackingInfo] = useState({});
  // Default: most recently changed customer first ("lastChange")
  const [listOrderFlag, setListOrderFlag] = useState("dateTime");
  const [listOrderDirectionFlag, setListOrderDirectionFlag] = useState("down");
  const [showInsertList, setShowInsertList] = useState(false);

  const [searchingFlag, setSearchingFlag] = useState(false);

  // Search filters
  const [SFirstName, setSFirstName] = useState("");
  const [Sphone, setSphone] = useState("");
  const [Smail, setSmail] = useState("");
  const [Ssex, setSsex] = useState("");
  const [SstatusList, setSstatusList] = useState([]); // status ids
  const [SintrestedInList, setSintrestedInList] = useState([]); // tag ids
  const [Ssearch, setSsearch] = useState("");
  const [to_date, setTo_date] = useState("");
  const [from_date, setFrom_date] = useState("");

  // For the editor's "already exists" check on new customers
  const [allEmails, setAllEmails] = useState({});
  const [allPhones, setAllPhones] = useState({});


  // Statuses for the inline status picker (fetched once on mount, as before)
  const [statusList, setStatusList] = useState([]);
  // Tags for the tag filter + bulk add-tag picker
  const tags = useLookupList(LIST.TAG);
  // Server WhatsApp connection — the row WhatsApp icon is green when connected, faded when not
  const whatsapp = useWhatsappStatus();
  const screens = useBreakpoint();

  useEffect(() => {
    getAllEmailsAndPhones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkRes = (results) => {
    if (results === "conError" || results === "err") {
      props.showAlert("אופס! קרתה תקלה... אנא נסו שוב מאוחר יותר");
      return false;
    }
    return true;
  };

  const getAllEmailsAndPhones = async () => {
    var results = await props.searchCustomers({});
    if (!checkRes(results)) return;
    var allEmails = {};
    var allPhones = {};
    results.forEach((info) => {
      if (info.mail) allEmails[info.mail.trim()] = "";
      if (info.phone) allPhones[info.phone.trim()] = "";
    });
    setAllEmails(allEmails);
    setAllPhones(allPhones);
    var statusList_ = await props.getDISTINCTListFromDB(
      "val,id",
      [{ key: "listName", val: ["customerStatus"] }],
      "customerStatus"
    );
    setStatusList(statusList_ || []);
  };

  const search = async () => {
    setSearchingFlag(true);

    var results = await props.searchCustomers({
      firstname: SFirstName,
      phone: Sphone,
      mail: Smail,
      sex: Ssex,
      from_date: from_date,
      to_date: to_date,
      statusList: SstatusList.filter(Boolean),
      status: "",
      intrestedInList: SintrestedInList.filter(Boolean),
      intrestedIn: "",
      search: Ssearch,
    });
    if (!checkRes(results)) return;

    results = results.map((item) => {
      return { ...item, check: true };
    });
    setSearchResults(results);
    setSearchingFlag(false);
    setHasSearched(true);
  };

  const clearFilters = () => {
    setSFirstName("");
    setSphone("");
    setSmail("");
    setSsex("");
    setSstatusList([]);
    setSintrestedInList([]);
    setSsearch("");
    setTo_date("");
    setFrom_date("");
  };

  const onOpenUpdater = (flag, tracking_info) => {
    if (flag !== "add") {
      tracking_info = JSON.parse(JSON.stringify(tracking_info));
      settrackingInfo(tracking_info);
    }
    setShowUpdater(true);
    setShowUpdaterFlag(flag);
  };

  // Header click: same column → flip direction, new column → "up" (ascending).
  const onClickOrder = (name) => {
    var DirectionFlag = "up";
    if (listOrderFlag === name) DirectionFlag = listOrderDirectionFlag === "up" ? "down" : "up";
    setListOrderFlag(name);
    setListOrderDirectionFlag(DirectionFlag);
  };

  // Sorts searchResults in place (as before) and returns it. Also feeds the CSV download.
  const getSortedRequests = () => {
    if (searchResults === "err") return [];

    var sortedRequests = [];
    const isUp = listOrderDirectionFlag === "up";
    if (listOrderFlag === "firstname" || listOrderFlag === "status" || listOrderFlag === "intrestedIn") {
      sortedRequests = searchResults.sort((a, b) => {
        if (a[listOrderFlag] > b[listOrderFlag]) {
          return -1 * (isUp ? -1 : 1);
        }
        if (b[listOrderFlag] > a[listOrderFlag]) {
          return isUp ? -1 : 1;
        }
        return 0;
      });
    } else if (listOrderFlag === "dateTime") {
      // "lastChange" (DB triggers keep it); entry date for the few rows that never got one
      const changedAt = (c) => new Date(c.lastChange || c.dateEnterSystem || 0);
      sortedRequests = searchResults.sort((a, b) => {
        return (changedAt(b) - changedAt(a)) * (isUp ? -1 : 1);
      });
    }

    return sortedRequests;
  };

  const onDeleteHandler = async (tracking_info, afterDelete) => {
    props.withConfirmAlert(async () => {
      await props.deleteFromList(tracking_info.id, "customers");
      if (afterDelete) afterDelete();
      search();
    });
  };

  const changeStatusList = () => {
    if (statusForChange === "" || statusIdForChange === null) {
      return props.showAlert("לא נבחר סטטוס");
    }
    searchResults
      .filter((_) => _.check)
      .forEach((cus) => {
        props.updateCustomerInfo({ key: "status_id", val: statusIdForChange, id: cus.id });
      });
    setStatusForChange("");
    setStatusIdForChange(null);
    setShowChangeListStatus(false);
    props.showAlert("שינוי הסטטוסים התבצע בהצלחה");
    search();
  };

  const changeTagsList = async () => {
    if (!selectedTagId) {
      return props.showAlert("לא נבחר טאג");
    }

    const selectedCustomerIds = searchResults.filter((_) => _.check).map((cus) => cus.id);

    if (selectedCustomerIds.length === 0) {
      return props.showAlert("לא נבחרו לקוחות");
    }

    try {
      const response = await fetch(`/api/tags/${selectedTagId}/customers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ customerIds: selectedCustomerIds }),
      });

      if (!response.ok) {
        throw new Error("Failed to add tag to customers");
      }

      setSelectedTagId(null);
      setShowChangeListTags(false);
      props.showAlert("הטאג נוסף בהצלחה ל-" + selectedCustomerIds.length + " לקוחות");
      search();
    } catch (error) {
      console.error("Error adding tag:", error);
      props.showAlert("שגיאה בהוספת טאג");
    }
  };

  // The table's checkbox column ↔ the rows' `check` flags.
  const selectedKeys = searchResults.filter((r) => r.check).map((r) => r.id);
  const setSelectedKeys = (keys) => {
    const keySet = new Set(keys);
    setSearchResults(searchResults.map((r) => ({ ...r, check: keySet.has(r.id) })));
  };
  const checkedIds = () =>
    searchResults
      .filter((item) => item.check)
      .map((info) => info.id)
      .join(",");


  const advancedCount = [SFirstName, Sphone, Smail, Ssex, from_date, to_date].filter((v) => v !== "").length;
  const sortedResults = getSortedRequests();
  const statusOptions = toOptions(statusList);
  const tagOptions = toOptions(tags.items);

  return (
    <Page>
      <PageHeader
        title="לקוחות"
        subtitle={hasSearched ? `${searchResults.length.toLocaleString("he-IL")} תוצאות` : null}
        actions={
          <>
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  { key: "import", icon: <UploadOutlined />, label: "הכנסת רשימת משתמשים" },
                  ...(searchResults.length !== 0
                    ? [
                        {
                          key: "csv",
                          icon: <DownloadOutlined />,
                          // CSV of ALL results in the current sort order (same as before)
                          label: (
                            <CSVLink headers={headers} filename={"רשימת תוצאות.csv"} data={sortedResults}>
                              הורד רשימת תוצאות
                            </CSVLink>
                          ),
                        },
                      ]
                    : []),
                ],
                onClick: ({ key }) => {
                  if (key === "import") setShowInsertList(true);
                },
              }}
            >
              <Button icon={<MoreOutlined />} aria-label="פעולות נוספות" />
            </Dropdown>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => onOpenUpdater("add")}>
              לקוח חדש
            </Button>
          </>
        }
      />

      <FilterBar
        onSearch={search}
        onClear={clearFilters}
        searching={searchingFlag}
        advancedCount={advancedCount}
        primary={
          <>
            <FilterField label="חיפוש חופשי" wide>
              <Input
                allowClear
                placeholder="שם, טלפון, מייל, היסטוריה…"
                value={Ssearch}
                onChange={(e) => setSsearch(e.target.value)}
              />
            </FilterField>
            <FilterField label="טאג">
              <Select
                mode="multiple"
                allowClear
                maxTagCount="responsive"
                placeholder="כל הטאגים"
                options={tagOptions}
                optionFilterProp="label"
                loading={tags.loading}
                value={SintrestedInList}
                onChange={setSintrestedInList}
              />
            </FilterField>
            <FilterField label="סטטוס">
              <Select
                mode="multiple"
                allowClear
                maxTagCount="responsive"
                placeholder="כל הסטטוסים"
                options={statusOptions}
                optionFilterProp="label"
                value={SstatusList}
                onChange={setSstatusList}
              />
            </FilterField>
          </>
        }
        advanced={
          <>
            <FilterField label="שם">
              <Input allowClear value={SFirstName} onChange={(e) => setSFirstName(e.target.value)} />
            </FilterField>
            <FilterField label="טלפון">
              <Input
                allowClear
                inputMode="tel"
                value={Sphone}
                onChange={(e) => setSphone(e.target.value.replace(/\D/g, ""))}
              />
            </FilterField>
            <FilterField label="מייל">
              <Input allowClear value={Smail} onChange={(e) => setSmail(e.target.value)} />
            </FilterField>
            <FilterField label="מין">
              <Input allowClear value={Ssex} onChange={(e) => setSsex(e.target.value)} />
            </FilterField>
            <FilterField label="מתאריך">
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                value={from_date ? dayjs(from_date, DATE_FORMAT) : null}
                onChange={(d) => setFrom_date(d ? d.format(DATE_FORMAT) : "")}
              />
            </FilterField>
            <FilterField label="עד תאריך">
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                value={to_date ? dayjs(to_date, DATE_FORMAT) : null}
                onChange={(d) => setTo_date(d ? d.format(DATE_FORMAT) : "")}
              />
            </FilterField>
          </>
        }
      />

      <DataTable
        data={sortedResults}
        loading={searchingFlag}
        sort={{ key: listOrderFlag, order: listOrderDirectionFlag === "up" ? "ascend" : "descend" }}
        onSort={onClickOrder}
        selection={{ selectedKeys, onChange: setSelectedKeys }}
        onRowClick={(row) => onOpenUpdater("update", row)}
        empty={
          hasSearched ? (
            <EmptyState
              title="לא נמצאו לקוחות"
              description="נסו לשנות או לנקות את המסננים"
              action={<Button onClick={clearFilters}>נקה מסננים</Button>}
            />
          ) : (
            <EmptyState title="חפשו לקוחות" description="הקלידו בחיפוש או בחרו מסננים ולחצו ״חפש״" />
          )
        }
        columns={buildColumns()}
      />

      <BulkActionBar
        count={selectedKeys.length}
        total={searchResults.length}
        onClear={() => setSelectedKeys([])}
        actions={[
          { key: "status", icon: <SwapOutlined />, label: "שינוי סטטוס", onClick: () => setShowChangeListStatus(true) },
          { key: "tag", icon: <TagOutlined />, label: "הוספת טאג", onClick: () => setShowChangeListTags(true) },
          {
            key: "message",
            icon: <MessageOutlined />,
            label: "שליחת הודעה",
            onClick: () => props.setShowSendMassage(true, checkedIds()),
          },
        ]}
      />

      {renderChangeListStatus()}
      {renderChangeListTags()}

      {showInsertList ? <InsertList close={() => setShowInsertList(false)} /> : null}

      {showUpdater ? (
        <CustomerDrawer
          allEmails={allEmails}
          allPhones={allPhones}
          info={trackingInfo}
          flag={showUpdaterFlag}
          statusOptions={statusOptions}
          tagOptions={tagOptions}
          actions={showUpdaterFlag === "add" ? null : customerActions(trackingInfo, { inEditor: true })}
          onClose={(flag) => {
            if (flag) search();
            setShowUpdater(false);
          }}
        />
      ) : null}
    </Page>
  );

  // ---- Table columns -------------------------------------------------------
  function buildColumns() {
    return [
      {
        key: "firstname",
        title: "שם",
        sortable: true,
        width: 190,
        render: (_, row) => (
          <div className={classes.nameCell}>
            <span className={classes.name}>{row.firstname + " " + (row.lastname || "")}</span>
            <span className={classes.contact}>{[row.phone, row.mail].filter(Boolean).join(" · ")}</span>
          </div>
        ),
      },
      {
        key: "status",
        title: "סטטוס",
        sortable: true,
        responsive: ["md"],
        width: 155,
        render: (_, row) => (
          // Inline status change — saves immediately, same as the old inline ListPicker.
          <Select
            variant="borderless"
            size="small"
            className={classes.statusSelect}
            popupMatchSelectWidth={false}
            value={row.status_id || undefined}
            placeholder={<StatusBadge>{row.status}</StatusBadge>}
            options={statusOptions}
            labelRender={({ value, label }) => <StatusBadge color={statusColor(value)}>{label || row.status}</StatusBadge>}
            optionRender={(opt) => <StatusBadge color={statusColor(opt.value)}>{opt.label}</StatusBadge>}
            onClick={(e) => e.stopPropagation()}
            onChange={(id) => {
              props.updateCustomerInfo({ key: "status_id", val: id, id: row.id });
              search();
            }}
          />
        ),
      },
      {
        key: "intrestedIn",
        title: "טאג",
        sortable: true,
        responsive: ["md"],
        // no width: shares the spare space equally with "last history" (up to 4 chips, then "+N")
        render: (_, row) =>
          row.tagDetails && row.tagDetails.length > 0 ? (
            <TagChips tags={row.tagDetails} max={4} />
          ) : (
            row.intrestedIn || <span className={classes.muted}>—</span>
          ),
      },
      {
        key: "dateTime",
        title: "שינוי אחרון",
        sortable: true,
        responsive: ["xl"],
        width: 115,
        render: (_, row) => <span className="ui-tabular">{dateToString(row.lastChange || row.dateEnterSystem)}</span>,
      },
      {
        key: "lastHistory",
        title: "היסטוריה אחרונה",
        responsive: ["lg"],
        // no width: shares the spare space equally with the tags column. `ellipsis` switches
        // the table to a fixed layout, so the fixed widths are kept and long text gets "…".
        ellipsis: true,
        render: (_, row) => renderLastHistory(row),
      },
      {
        key: "actions",
        title: "",
        align: "left",
        // desktop: a row of icons (4, + call on touch devices); phones: a single "⋯" menu
        width: screens.md === false ? 64 : IS_TOUCH ? 175 : 150,
        render: (_, row) => <RowActions actions={customerActions(row)} />,
      },
    ];
  }

  // One list of actions per customer, shared by the table row and the editor's "⋯" menu.
  // inEditor: leaves out "edit" (you're already in it) and closes the editor after a delete.
  function customerActions(row, { inEditor = false } = {}) {
    return [
      {
        key: "call",
        icon: <PhoneOutlined />,
        label: "חיוג",
        href: "tel:" + row.phone,
        // A phone link only helps on a phone (opens the dialer) — touch devices only,
        // like the old screen, which showed it below 500px width.
        hidden: !row.phone || !IS_TOUCH,
      },
      {
        key: "whatsapp",
        icon: <WhatsAppOutlined />,
        label: whatsapp.connected ? "WhatsApp" : whatsapp.label,
        color: tokens.color.whatsapp,
        muted: !whatsapp.connected,
        hidden: !validationPhoneNumber(row.phone),
        // Opens the CRM's own send-message modal on its WhatsApp tab (sent through the
        // server's WhatsApp queue) — not a wa.me web link.
        onClick: () => props.setShowSendMassage(true, row.id + "", "WHATSAPP"),
      },
      {
        key: "message",
        icon: <MessageOutlined />,
        label: "שליחת מייל",
        onClick: () => props.setShowSendMassage(true, row.id + ""),
      },
      {
        key: "edit",
        icon: <EditOutlined />,
        label: "עריכה",
        hidden: inEditor,
        onClick: () => onOpenUpdater("update", row),
      },
      {
        key: "delete",
        icon: <DeleteOutlined />,
        label: "מחיקה",
        danger: true,
        onClick: () => onDeleteHandler(row, inEditor ? () => setShowUpdater(false) : undefined),
      },
    ];
  }

  // Last history entry: one line + date; full text on hover.
  function renderLastHistory(row) {
    if (!row.LastHistoryVal) return <span className={classes.muted}>—</span>;
    const text = row.LastHistoryVal.split("##").join("\n");
    return (
      <Tooltip title={<div className={classes.tooltipText}>{text}</div>} placement="top" mouseEnterDelay={0.3}>
        <div className={classes.historyCell}>
          <span className={`${classes.historyDate} ui-tabular`}>
            {row.LastHistoryDateTime ? dateToString(row.LastHistoryDateTime) : "dd/mm/yyyy"}
          </span>
          <span className={classes.historyText}>{text}</span>
        </div>
      </Tooltip>
    );
  }

  // ---- Bulk status / tag dialogs -------------------------------------------
  function renderChangeListStatus() {
    const selectedCount = searchResults.filter((_) => _.check).length;
    return (
      <Modal
        open={showChangeListStatus}
        title="שינוי סטטוס"
        okText="אישור שינוי"
        cancelText="ביטול"
        onOk={changeStatusList}
        onCancel={() => setShowChangeListStatus(false)}
        destroyOnHidden
      >
        <p className={classes.modalInfo}>
          שינוי סטטוס עבור <strong>{selectedCount}</strong> לקוחות נבחרים
        </p>
        <Select
          style={{ width: "100%" }}
          showSearch
          optionFilterProp="label"
          placeholder="בחר סטטוס…"
          options={statusOptions}
          value={statusIdForChange === null ? undefined : statusIdForChange}
          optionRender={(opt) => <StatusBadge color={statusColor(opt.value)}>{opt.label}</StatusBadge>}
          onChange={(id, opt) => {
            setStatusForChange(itemLabel(opt.item));
            setStatusIdForChange(id);
          }}
        />
      </Modal>
    );
  }

  function renderChangeListTags() {
    const selectedCount = searchResults.filter((_) => _.check).length;
    return (
      <Modal
        open={showChangeListTags}
        title="הוספת טאג"
        okText="הוסף טאג"
        cancelText="ביטול"
        onOk={changeTagsList}
        onCancel={() => setShowChangeListTags(false)}
        destroyOnHidden
      >
        <p className={classes.modalInfo}>
          הוספת טאג עבור <strong>{selectedCount}</strong> לקוחות נבחרים
        </p>
        <Select
          style={{ width: "100%" }}
          showSearch
          optionFilterProp="label"
          placeholder="בחר טאג…"
          options={tagOptions}
          loading={tags.loading}
          value={selectedTagId === null ? undefined : selectedTagId}
          onChange={(id) => setSelectedTagId(id)}
        />
      </Modal>
    );
  }
};

const mapStateToProps = () => {
  return {};
};
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      showAlert: actionsCreators.showAlert,
      setShowSendMassage: actionsCreators.setShowSendMassage,
      searchCustomers: actionsCreators.searchCustomers,
      deleteFromList: actionsCreators.deleteFromList,
      withConfirmAlert: actionsCreators.withConfirmAlert,
      updateCustomerInfo: actionsCreators.updateCustomerInfo,
      getDISTINCTListFromDB: actionsCreators.getDISTINCTListFromDB,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(Customers);
